import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { ExportButton } from '../components/ExportButton';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Copy, Download, Trash2, QrCode, ExternalLink, Sparkles, Image as ImageIcon, Check, Filter, Search } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { StateCitySelect } from '../components/StateCitySelect';
import { useSettings } from '../hooks/useSettings';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useQrCode } from '../hooks/useQrCode';
import { ImageUpload } from '../components/ImageUpload';
import { LinkMetaSheet } from '../components/LinkMetaSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LinksPage = () => {
  const { settings } = useSettings();
  const { canEdit } = usePermissions();
  const { activeTenant } = useAuth();
  const canEditLinks = canEdit('links');
  const tiposAcao = settings?.link_tipos_acao || [];
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const buildPublicUrl = (token) => `${apiBase}/api/og/r/${activeTenant?.slug}/${token}`;
  const [links, setLinks] = useState([]);
  const [liderancas, setLiderancas] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [dobradas, setDobradas] = useState([]);
  const [filterDobrada, setFilterDobrada] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const selectedShareUrl = selectedLink ? `${apiBase}/api/og/r/${activeTenant?.slug}/${selectedLink.token}` : '';
  const liveQrDataUrl = useQrCode(selectedShareUrl);

  // OG meta editing (lateral sheet) — UI extracted into <LinkMetaSheet>.
  // We only keep the open/close + selected-link state here.
  const [metaSheetOpen, setMetaSheetOpen] = useState(false);
  const [metaLink, setMetaLink] = useState(null);

  const openMetaSheet = (link) => {
    setMetaLink(link);
    setMetaSheetOpen(true);
  };

  const [formData, setFormData] = useState({
    token: '',
    lideranca_id: '',
    destino_id: '',
    tipo_acao: 'canal',
    estado: '',
    cidade: '',
    bairro: '',
    campanha: '',
    status: 'ativo',
    observacoes: '',
    capturar_dados: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [linksRes, liderancasRes, destinosRes, dobradasRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/links`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leaders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/destinations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/dobradas`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLinks(linksRes.data);
      setLiderancas(liderancasRes.data);
      setDestinos(destinosRes.data);
      setDobradas(dobradasRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Map leader → dobrada_id for quick lookup when filtering
  const [filterLideranca, setFilterLideranca] = useState('');
  const [filterText, setFilterText] = useState('');

  const leaderDobradaMap = React.useMemo(() => {
    const map = {};
    liderancas.forEach((l) => {
      map[l.id] = l.dobrada_id || null;
    });
    return map;
  }, [liderancas]);

  // Fast leader lookup for text search (id → apelido/nome tokens).
  const leaderTextMap = React.useMemo(() => {
    const map = {};
    liderancas.forEach((l) => {
      map[l.id] = `${l.apelido || ''} ${l.nome || l.nome_completo || ''}`.toLowerCase();
    });
    return map;
  }, [liderancas]);

  const filteredLinks = React.useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return links.filter((link) => {
      // Dobrada filter
      if (filterDobrada) {
        const dobradaId = leaderDobradaMap[link.lideranca_id];
        if (filterDobrada === '__principal__') {
          if (dobradaId) return false;
        } else if (dobradaId !== filterDobrada) return false;
      }
      // Leader filter
      if (filterLideranca && link.lideranca_id !== filterLideranca) return false;
      // Text search: token, leader name, campanha, og_title, observacoes
      if (q) {
        const haystack = [
          link.token,
          link.campanha,
          link.og_title,
          link.observacoes,
          link.destination_name,
          leaderTextMap[link.lideranca_id] || '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [links, filterDobrada, filterLideranca, filterText, leaderDobradaMap, leaderTextMap]);

  // Leaders limited to the currently-selected dobrada (nice UX: filtering by
  // dobrada narrows the leader dropdown automatically).
  const availableLeaders = React.useMemo(() => {
    if (!filterDobrada) return liderancas;
    if (filterDobrada === '__principal__') return liderancas.filter((l) => !l.dobrada_id);
    return liderancas.filter((l) => l.dobrada_id === filterDobrada);
  }, [liderancas, filterDobrada]);

  // Reset leader filter when dobrada filter changes and the current leader is not in the new subset.
  useEffect(() => {
    if (filterLideranca && !availableLeaders.some((l) => l.id === filterLideranca)) {
      setFilterLideranca('');
    }
  }, [availableLeaders, filterLideranca]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.estado) payload.estado = null;
      if (!payload.bairro) payload.bairro = null;
      if (!payload.campanha) payload.campanha = null;
      if (!payload.observacoes) payload.observacoes = null;
      await axios.post(`${BACKEND_URL}/api/links`, payload, {
        withCredentials: true,
      });
      toast.success('Link criado com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erro ao criar link';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este link?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/links/${id}`, {
        withCredentials: true,
      });
      toast.success('Link excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir link');
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const handleDownloadQR = (link) => {
    setSelectedLink(link);
    setQrDialogOpen(true);
  };

  const downloadQRImage = () => {
    if (!liveQrDataUrl && !selectedLink?.qr_code) return;
    const link = document.createElement('a');
    link.href = liveQrDataUrl || selectedLink.qr_code;
    link.download = `qr-${selectedLink.token}.png`;
    link.click();
    toast.success('QR Code baixado!');
  };

  const resetForm = () => {
    setFormData({
      token: '',
      lideranca_id: '',
      destino_id: '',
      tipo_acao: 'canal',
      estado: '',
      cidade: '',
      bairro: '',
      campanha: '',
      status: 'ativo',
      observacoes: '',
      capturar_dados: false,
    });
  };

  const generateToken = () => {
    const random = Math.random().toString(36).substring(2, 10);
    setFormData({ ...formData, token: random });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="links-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Links Personalizados</h1>
            <p className="text-muted-foreground mt-2">Crie e gerencie links curtos e QR Codes</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton resource="links" />
            <Button onClick={() => setDialogOpen(true)} data-testid="create-link-btn" disabled={!canEditLinks}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Link
            </Button>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                <Filter className="h-3.5 w-3.5" />
                Filtros
              </div>

              {/* Text search */}
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Buscar por token, liderança, campanha…"
                  className="h-9 pl-8 text-sm"
                  data-testid="links-filter-text"
                />
              </div>

              {/* Dobrada */}
              {dobradas.length > 0 && (
                <Select
                  value={filterDobrada || 'todas'}
                  onValueChange={(v) => setFilterDobrada(v === 'todas' ? '' : v)}
                >
                  <SelectTrigger className="w-52 h-9" data-testid="links-filter-dobrada">
                    <SelectValue placeholder="Dobrada" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas as dobradas</SelectItem>
                    <SelectItem value="__principal__">Candidato principal</SelectItem>
                    {dobradas.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Liderança */}
              <Select
                value={filterLideranca || 'todas'}
                onValueChange={(v) => setFilterLideranca(v === 'todas' ? '' : v)}
              >
                <SelectTrigger className="w-64 h-9" data-testid="links-filter-lideranca">
                  <SelectValue placeholder="Liderança" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="todas">
                    Todas as lideranças ({availableLeaders.length})
                  </SelectItem>
                  {availableLeaders.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.apelido ? `${l.apelido} — ` : ''}{l.nome || l.nome_completo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Result count + clear */}
              {(filterDobrada || filterLideranca || filterText) && (
                <>
                  <span className="text-xs text-slate-500" data-testid="links-filter-count">
                    {filteredLinks.length} de {links.length} links
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-slate-500 hover:text-slate-900"
                    onClick={() => {
                      setFilterDobrada('');
                      setFilterLideranca('');
                      setFilterText('');
                    }}
                    data-testid="links-filter-clear"
                  >
                    Limpar
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table data-testid="links-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Token</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Liderança</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Tipo</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Captação</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Status</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider text-right">Acessos</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider text-right">Redirects</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Meta</TableHead>
                  <TableHead className="font-bold text-slate-700 uppercase text-[10px] tracking-wider text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLinks.map((link) => {
                  const lideranca = liderancas.find((l) => l.id === link.lideranca_id);
                  const hasCustomMeta = !!(
                    link.og_title ||
                    link.og_description ||
                    link.og_image_url ||
                    link.capture_hero_image_url ||
                    link.capture_subtitle
                  );
                  return (
                    <TableRow key={link.id} data-testid={`link-row-${link.id}`} className="hover:bg-slate-50/60">
                      <TableCell className="font-mono text-xs text-slate-800 font-semibold">
                        /{link.token}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {lideranca?.nome_completo || <span className="text-slate-400 italic">—</span>}
                      </TableCell>
                      <TableCell className="text-sm capitalize text-slate-700">{link.tipo_acao}</TableCell>
                      <TableCell>
                        {link.capturar_dados ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold text-[10px]">
                            Com formulário
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                            Direto
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold capitalize ${
                            link.status === 'ativo' ? 'text-emerald-700' : 'text-slate-500'
                          }`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${link.status === 'ativo' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          {link.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-800 tabular-nums">
                        {link.stats?.total_acessos ?? 0}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-800 tabular-nums">
                        {link.stats?.total_redirecionamentos ?? 0}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant={hasCustomMeta ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => openMetaSheet(link)}
                          disabled={!canEditLinks}
                          className={`h-7 gap-1.5 text-[11px] ${hasCustomMeta ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                          data-testid={`edit-meta-btn-${link.id}`}
                          title="Personalizar título, descrição e imagem deste link"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          {hasCustomMeta ? 'Personalizada' : 'Padrão'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleCopyLink(buildPublicUrl(link.token))}
                            data-testid={`copy-link-btn-${link.id}`}
                            title="Copiar link"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDownloadQR(link)}
                            data-testid={`download-qr-btn-${link.id}`}
                            title="QR Code"
                          >
                            <QrCode className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => window.open(buildPublicUrl(link.token), '_blank')}
                            data-testid={`test-link-btn-${link.id}`}
                            title="Testar link"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => handleDelete(link.id)}
                            data-testid={`delete-link-btn-${link.id}`}
                            disabled={!canEditLinks}
                            title="Excluir"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {links.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Nenhum link criado ainda.</p>
              <Button onClick={() => setDialogOpen(true)} className="mt-4" disabled={!canEditLinks}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Link
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Novo Link Personalizado</DialogTitle>
              <DialogDescription>
                Crie um link curto para uma liderança
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="token">Token do Link</Label>
                  <div className="flex gap-2">
                    <Input
                      id="token"
                      value={formData.token}
                      onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                      placeholder="ex: ana-garavelo"
                      required
                      data-testid="input-token"
                    />
                    <Button type="button" variant="outline" onClick={generateToken}>
                      Gerar
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Link final: {apiBase}/api/og/r/{activeTenant?.slug || '[cliente]'}/{formData.token || '[token]'}
                  </p>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="lideranca_id">Liderança</Label>
                  <Select
                    value={formData.lideranca_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, lideranca_id: value })
                    }
                  >
                    <SelectTrigger data-testid="select-lideranca">
                      <SelectValue placeholder="Selecione uma liderança" />
                    </SelectTrigger>
                    <SelectContent>
                      {liderancas.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.nome_completo}{l.apelido && ` (@${l.apelido})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="destino_id">Destino</Label>
                  <Select
                    value={formData.destino_id}
                    onValueChange={(value) => setFormData({ ...formData, destino_id: value })}
                  >
                    <SelectTrigger data-testid="select-destino">
                      <SelectValue placeholder="Selecione um destino" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinos.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.nome} ({d.tipo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="tipo_acao">Tipo de Ação</Label>
                  <Select
                    value={formData.tipo_acao}
                    onValueChange={(value) => setFormData({ ...formData, tipo_acao: value })}
                  >
                    <SelectTrigger data-testid="select-tipo-acao">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposAcao.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <StateCitySelect
                    estado={formData.estado}
                    cidade={formData.cidade}
                    onEstadoChange={(v) => setFormData({ ...formData, estado: v, cidade: '' })}
                    onCidadeChange={(v) => setFormData({ ...formData, cidade: v })}
                    required
                    allowedStates={settings?.allowed_states}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    placeholder="Opcional"
                    data-testid="input-bairro"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="campanha">Campanha</Label>
                  <Input
                    id="campanha"
                    value={formData.campanha}
                    onChange={(e) => setFormData({ ...formData, campanha: e.target.value })}
                    placeholder="Opcional"
                    data-testid="input-campanha"
                  />
                </div>
                <div className="col-span-2 space-y-3 border border-border rounded-sm p-4 bg-secondary/30">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <Label htmlFor="capturar_dados" className="text-base font-bold">
                        Capturar dados do apoiador?
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formData.capturar_dados
                          ? 'O apoiador preencherá nome e WhatsApp antes de ser redirecionado'
                          : 'Redirecionamento direto, sem captação de dados'}
                      </p>
                    </div>
                    <Switch
                      id="capturar_dados"
                      checked={formData.capturar_dados}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, capturar_dados: checked })
                      }
                      data-testid="switch-capturar-dados"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="submit-link-btn">
                  Criar Link
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>QR Code</DialogTitle>
              <DialogDescription>
                Link: {selectedLink ? buildPublicUrl(selectedLink.token) : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-6">
              {(liveQrDataUrl || selectedLink?.qr_code) && (
                <img
                  src={liveQrDataUrl || selectedLink.qr_code}
                  alt="QR Code"
                  className="w-64 h-64 border border-border rounded-sm"
                />
              )}
            </div>
            <DialogFooter>
              <Button onClick={downloadQRImage} data-testid="download-qr-image-btn">
                <Download className="h-4 w-4 mr-2" />
                Baixar QR Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* OG Metadata Sheet (lateral panel) — extracted into LinkMetaSheet */}
        <LinkMetaSheet
          link={metaLink}
          leader={metaLink ? liderancas.find((l) => l.id === metaLink.lideranca_id) || null : null}
          open={metaSheetOpen}
          onOpenChange={(v) => {
            setMetaSheetOpen(v);
            if (!v) setMetaLink(null);
          }}
          onSaved={fetchData}
          canEdit={canEditLinks}
        />
      </div>
    </Layout>
  );
};
